// Status-0 / Axios "Network Error" decisions for checkout shipping quotes and cart sync.
// No Vue, axios, or Mango imports — node tests load this file directly.
// A missing response means the server may never have seen the request, so callers
// must not treat it like a BigCommerce rejection (do not wipe the cart, do not
// blame the address).

export const CART_SYNC_NETWORK_ATTEMPTS = 3

export const CART_SYNC_NETWORK_MESSAGE =
	'We could not reach the server to sync your cart. Your items are still on this device.'

export const SHIPPING_NETWORK_FAILURE = {
	kind: 'network',
	retry: true,
	title: 'Could not load shipping options',
	text: 'The connection dropped before shipping rates came back. Your address is still saved.',
	retryLabel: 'Try again',
	dismissLabel: 'Close',
}

const SHIPPING_OTHER_FAILURE = {
	kind: 'other',
	retry: false,
	title: 'Error',
	text: 'There was an error getting shipping estimates. Please verify your address info is accurate and try again.',
	retryLabel: 'OK',
	dismissLabel: null,
}

function numericStatus(value) {
	if (value == null || value === '') return null
	const status = Number(value)
	return Number.isFinite(status) ? status : null
}

/**
 * Axios reports a dropped connection as Network Error with no response.
 * Browsers surface that as HTTP status 0. A real HTTP status is not this case.
 * Cancelled requests are the page-load abort pattern and are left alone.
 */
export function isStatus0NetworkError(error) {
	if (!error || typeof error !== 'object') return false
	if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') return false

	const responseStatus = numericStatus(error.response?.status)
	if (responseStatus != null && responseStatus !== 0) return false
	if (responseStatus === 0) return true

	const requestStatus = numericStatus(error.request?.status ?? error.status)
	if (requestStatus === 0 && error.response == null) return true
	if (error.code === 'ERR_NETWORK') return true
	if (error.response == null && /network error/i.test(String(error.message || ''))) return true
	return false
}

export function shippingEstimateFailure(error) {
	if (isStatus0NetworkError(error)) return { ...SHIPPING_NETWORK_FAILURE }
	return { ...SHIPPING_OTHER_FAILURE }
}

export function cartSyncFailureDisposition(error) {
	if (!isStatus0NetworkError(error)) {
		return { network: false, preserveCart: false, message: null }
	}
	return { network: true, preserveCart: true, message: CART_SYNC_NETWORK_MESSAGE }
}

function defaultSleep(attempt) {
	const ms = 200 * attempt
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retry only status-0 / network failures, at most `attempts` times (including
 * the first try). Any other error returns immediately. Never loops forever.
 */
export async function retryStatus0Network(operation, { attempts = CART_SYNC_NETWORK_ATTEMPTS, sleep = defaultSleep, shouldAbort = () => false } = {}) {
	const total = Math.max(1, Number(attempts) || 1)
	let lastError = null

	for (let attempt = 1; attempt <= total; attempt++) {
		if (shouldAbort()) {
			return { ok: false, aborted: true, error: lastError, network: isStatus0NetworkError(lastError), attempts: attempt - 1 }
		}
		try {
			return { ok: true, aborted: false, value: await operation(), attempts: attempt }
		} catch (error) {
			lastError = error
			const network = isStatus0NetworkError(error)
			if (!network || attempt === total) {
				return { ok: false, aborted: false, error, network, attempts: attempt }
			}
			await sleep(attempt)
		}
	}

	return { ok: false, aborted: false, error: lastError, network: isStatus0NetworkError(lastError), attempts: total }
}
